import React, { useEffect, useMemo, useState } from "react";
import { Popper, Portal, ClickAwayListener } from "@material-ui/core";
import { matchesSearch, contactSearchFields } from "../../../utils/search";

// Who the lesson is for, picked from a wide panel beside the booking form:
// every student at once, in a column per place they dance and a column per
// card only some people have (Indiana). On a phone it is the whole screen.
// Stefan, calls 6 and 8: "on the side here... Naperville students...
// Oakbrook students and Indiana students... if they're under you... click
// it on one of the students and that's it." FirstClass is the only system
// he has seen do it, "and it's genius".

const INK = "#111827";
const MUTED = "#666666";
const LINE = "#E5E7EB";
const font = "'Nunito', 'Varela Round', sans-serif";

const fullName = (s) => `${s.attributes.first_name || ""} ${s.attributes.last_name || ""}`.trim();
const byName = (a, b) => fullName(a).localeCompare(fullName(b));

// The panel's columns: each opt-in card's people first (they are the
// exceptions), then each place, then anybody who has no lessons yet.
export const groupStudents = ({ students, places = {}, addresses = [], categories = [] }) => {
  const optIn = categories.filter((c) => c.opt_in && c.people);
  const placeName = (id) => {
    const a = addresses.find((x) => String(x.id) === String(id));
    const at = (a && a.attributes) || {};
    return at.city || at.street || "";
  };
  const groups = [];
  const taken = new Set();
  optIn.forEach((c) => {
    const leadIds = (c.people.lead_ids || []).map(String);
    const userIds = (c.people.user_ids || []).map(String);
    const people = students.filter((s) =>
      (s.studentType === "lead" ? leadIds : userIds).includes(String(s.id))
    );
    if (people.length === 0) return;
    people.forEach((s) => taken.add(s.id));
    groups.push({ key: `card-${c.id}`, title: c.name, color: c.color, people });
  });
  const rest = students.filter((s) => !taken.has(s.id));
  const placeIds = addresses.length > 1 ? addresses.map((a) => String(a.id)) : [];
  // Where they usually dance; where they last danced if that is all we know.
  const placeOf = (s) => String(places[s.id] || (s.attributes && s.attributes.last_studio_address_id) || "");
  placeIds.forEach((id) => {
    const people = rest.filter((s) => placeOf(s) === id);
    if (people.length) groups.push({ key: `place-${id}`, title: placeName(id), people });
  });
  const unplaced = rest.filter((s) => !placeIds.includes(placeOf(s)));
  if (unplaced.length) {
    groups.push({
      key: "rest",
      title: groups.length ? (placeIds.length ? "No lessons yet" : "Everyone else") : "Students",
      people: unplaced,
    });
  }
  return groups;
};

const NameButton = ({ student, onPick, mine }) => (
  <button
    type="button"
    data-testid="student-panel-name"
    onClick={() => onPick(student)}
    className="juice-row"
    style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%",
      textAlign: "left", border: 0, background: "transparent", padding: "7px 10px", borderRadius: 10,
      fontFamily: font, fontSize: 14.5, fontWeight: 600, color: INK, cursor: "pointer",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = "#F3F4F6"; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
  >
    <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{fullName(student) || "Unnamed"}</span>
    {mine && <span aria-label="Their teacher" title="Their teacher" style={{ flex: "0 0 auto", width: 7, height: 7, borderRadius: "50%", background: "#007BFF" }} />}
  </button>
);

const StudentPanel = ({
  open,
  anchorEl,
  onClose,
  onPick,
  students,
  instructorName,
  places,
  addresses,
  categories,
}) => {
  const [query, setQuery] = useState("");
  // Beside the form (500 px and its padding, 564 in all) when the screen
  // has room for a real panel on its right; otherwise the panel takes the
  // screen and Back returns.
  const roomBeside = () => (typeof window === "undefined" ? 640 : Math.min(640, (window.innerWidth - 564) / 2 - 30));
  const [wide, setWide] = useState(() => roomBeside() >= 400);
  const [viewport, setViewport] = useState(() => (typeof window === "undefined" ? 1280 : window.innerWidth));
  const hasMine = students.some((s) => s.taughtByCurrent);
  const [onlyMine, setOnlyMine] = useState(hasMine);

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    setOnlyMine(students.some((s) => s.taughtByCurrent));
    const onResize = () => {
      setWide(roomBeside() >= 400);
      setViewport(window.innerWidth);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // Reset each time it opens, not when the list behind it changes.
  }, [open]);

  const groups = useMemo(() => {
    const searching = query.trim().length > 0;
    // Searching looks through everyone, whichever list is showing.
    const pool = students
      .filter((s) => searching || !onlyMine || s.taughtByCurrent)
      .filter((s) => matchesSearch(query, contactSearchFields(s.attributes)))
      .sort(byName);
    return groupStudents({ students: pool, places, addresses, categories });
  }, [students, places, addresses, categories, query, onlyMine]);

  if (!open) return null;

  const total = groups.reduce((n, g) => n + g.people.length, 0);
  const panelWidth = wide ? roomBeside() : viewport;
  const columns = Math.max(1, Math.min(groups.length, 3, Math.floor(panelWidth / 200)));
  const body = (
    <div
      data-testid="student-panel"
      style={{
        background: "#ffffff", border: `1px solid ${LINE}`, borderRadius: wide ? 16 : 0,
        boxShadow: wide ? "0 12px 32px rgba(17,24,39,0.16)" : "none", fontFamily: font,
        display: "flex", flexDirection: "column",
        width: wide ? panelWidth : "100%",
        height: wide ? "min(640px, 85vh)" : "100%",
        boxSizing: "border-box",
        animation: "student-panel-in 160ms ease-out",
      }}
    >
      <style>{"@keyframes student-panel-in{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:none}}@media (prefers-reduced-motion: reduce){[data-testid=student-panel]{animation:none!important}}"}</style>
      <div style={{ padding: "16px 18px 10px", borderBottom: `1px solid ${LINE}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          {!wide && (
            <button type="button" onClick={onClose} style={{ border: 0, background: "none", fontSize: 15, fontWeight: 600, color: "#007BFF", padding: 0, cursor: "pointer", fontFamily: font }}>
              ← Back
            </button>
          )}
          <div style={{ fontSize: 17, fontWeight: 700, color: INK }}>Who is the lesson for?</div>
        </div>
        {/* Chrome read "email" in the old placeholder and offered the
            owner's own saved addresses over the form (Oct 29 2026). A search
            box, with no word that looks like a form to fill, and autofill
            and password managers told to leave it alone. Email and phone
            still match. */}
        <input
          autoFocus
          type="search"
          name="lesson-student-search"
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
          aria-label="Search students"
          data-testid="student-panel-search"
          placeholder="Search by name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 10, border: `1px solid ${LINE}`, fontSize: 15, fontFamily: font, appearance: "none", WebkitAppearance: "none" }}
        />
        {hasMine && (
          <div role="radiogroup" aria-label="Whose students" style={{ display: "flex", gap: 6, marginTop: 10 }}>
            {[
              [true, instructorName ? `${instructorName}'s students` : "Their students"],
              [false, "Everyone"],
            ].map(([value, label]) => (
              <button
                key={label}
                type="button"
                role="radio"
                aria-checked={onlyMine === value}
                data-testid={value ? "student-panel-mine" : "student-panel-everyone"}
                onClick={() => setOnlyMine(value)}
                style={{
                  border: `1px solid ${onlyMine === value ? INK : LINE}`, background: onlyMine === value ? INK : "#ffffff",
                  color: onlyMine === value ? "#ffffff" : INK, borderRadius: 999, padding: "6px 14px", fontSize: 13.5,
                  fontWeight: 600, fontFamily: font, cursor: "pointer",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div style={{ flex: "1 1 auto", overflowY: "auto", padding: "12px 12px 16px" }}>
        {total === 0 ? (
          <div style={{ padding: "18px 8px", color: MUTED, fontSize: 14.5 }}>
            {query ? `Nobody matches "${query}".` : "Nobody here yet."}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              gap: 14,
              alignItems: "start",
            }}
          >
            {groups.map((g) => (
              <section key={g.key} data-testid="student-panel-group" aria-label={g.title} style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 10px 6px", fontSize: 13, fontWeight: 700, color: MUTED }}>
                  {g.color && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: g.color }} />}
                  {g.title}
                  <span style={{ fontWeight: 600, color: "#9CA3AF" }}>{g.people.length}</span>
                </div>
                {g.people.map((s) => (
                  <NameButton key={s.id} student={s} onPick={onPick} mine={!onlyMine && s.taughtByCurrent} />
                ))}
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (!wide) {
    return (
      <Portal>
        <div style={{ position: "fixed", inset: 0, zIndex: 1302, background: "#ffffff" }}>{body}</div>
      </Portal>
    );
  }
  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="right-start"
      style={{ zIndex: 1302 }}
      modifiers={{ offset: { offset: "0,14" }, flip: { enabled: false }, preventOverflow: { boundariesElement: "viewport" } }}
    >
      <ClickAwayListener onClickAway={onClose}>{body}</ClickAwayListener>
    </Popper>
  );
};

export default StudentPanel;
